import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContextualHelp from "../../../components/dashboard/ContextualHelp";
import ResponsiveOperationalTable from "../../../components/dashboard/ResponsiveOperationalTable";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { useOutletContext, useParams } from "react-router-dom";
import { erpApi } from "../../../api/erp";
import { ErpEmpty, ErpLoading, ErpPanel, ErpSectionTitle } from "../../../components/dashboard/erp/ErpUi";
import { ACCOUNT_TYPE_LABELS, ERP_BRAND, entityId } from "../../../utils/erp";

const emptyForm = {
  code: "",
  name: "",
  type: "asset",
  normalSide: "debit",
  active: true,
  description: "",
};

export default function ProjectErpAccountsPage() {
  const { projectId } = useParams();
  const { refreshBootstrap } = useOutletContext();
  const [accounts, setAccounts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [form, setForm] = React.useState(emptyForm);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await erpApi.listAccounts(projectId);
      setAccounts(data.accounts || []);
    } catch (err) {
      setError(err.message || "No se pudieron cargar las cuentas contables");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (account) => {
    setEditing(account);
    setForm({
      code: account.code || "",
      name: account.name || "",
      type: account.type || "asset",
      normalSide: account.normalSide || "debit",
      active: account.active !== false,
      description: account.description || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      setError("Completá el código y el nombre de la cuenta.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      if (editing) {
        await erpApi.updateAccount(projectId, entityId(editing), {
          code: form.code.toUpperCase(),
          name: form.name,
          description: form.description,
          active: form.active,
        });
      } else {
        await erpApi.createAccount(projectId, {
          ...form,
          code: form.code.toUpperCase(),
        });
      }
      setDialogOpen(false);
      await Promise.all([load(), refreshBootstrap()]);
    } catch (err) {
      setError(err.message || "No se pudo guardar la cuenta");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (account) => {
    try {
      setError("");
      await erpApi.updateAccount(projectId, entityId(account), { active: !account.active });
      await Promise.all([load(), refreshBootstrap()]);
    } catch (err) {
      setError(err.message || "No se pudo actualizar la cuenta");
    }
  };

  return (
    <Stack spacing={2.4}>
      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}
      <ErpSectionTitle
        title="Plan de cuentas"
        description="Cuentas usadas por movimientos y asientos contables."
        action={
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={load} sx={{ textTransform: "none", fontWeight: 850 }}>Actualizar</Button>
            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate} sx={{ textTransform: "none", fontWeight: 850 }}>Nueva cuenta</Button>
          </Stack>
        }
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: -1 }}>
        <ContextualHelp
          compact
          title="Cuentas del sistema"
          text="Las cuentas base conservan su código para mantener íntegros los asientos existentes."
          bullets={["Podés cambiar nombre o descripción.", "Si una cuenta deja de usarse, desactivala en lugar de borrar su historial."]}
        />
      </Box>

      <ErpPanel>
        {loading ? <ErpLoading label="Cargando plan de cuentas..." /> : !accounts.length ? <ErpEmpty title="Sin cuentas contables" description="Prepará la estructura contable o creá la primera cuenta manual." /> : (
          <TableContainer>
            <ResponsiveOperationalTable>
              <TableHead>
                <TableRow>
                  <TableCell>Código</TableCell>
                  <TableCell>Nombre</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Naturaleza</TableCell>
                  <TableCell>Origen</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {accounts.map((account) => (
                  <TableRow key={entityId(account)} hover>
                    <TableCell sx={{ fontWeight: 950 }}>{account.code}</TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 850 }}>{account.name}</Typography>
                      {account.description ? <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{account.description}</Typography> : null}
                    </TableCell>
                    <TableCell><Chip size="small" label={ACCOUNT_TYPE_LABELS[account.type] || account.type} /></TableCell>
                    <TableCell>{account.normalSide === "debit" ? "Deudora" : "Acreedora"}</TableCell>
                    <TableCell><Chip size="small" color={account.system ? "primary" : "default"} label={account.system ? "Sistema" : "Personalizada"} /></TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Switch size="small" checked={account.active !== false} onChange={() => toggleActive(account)} />
                        <Typography variant="body2">{account.active !== false ? "Activa" : "Inactiva"}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar cuenta"><IconButton size="small" onClick={() => openEdit(account)}><EditRoundedIcon fontSize="small" /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </ResponsiveOperationalTable>
          </TableContainer>
        )}
      </ErpPanel>

      <Dialog open={dialogOpen} onClose={saving ? undefined : () => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950 }}>{editing ? "Editar cuenta" : "Nueva cuenta contable"}</DialogTitle>
        <Divider />
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="Código" required value={form.code} disabled={Boolean(editing?.system)} onChange={(event) => setForm((current) => ({ ...current, code: event.target.value.toUpperCase() }))} helperText={editing?.system ? "El código de una cuenta del sistema no puede cambiarse." : "Usá mayúsculas y guiones bajos, por ejemplo GASTOS_PUBLICIDAD."} />
            <TextField label="Nombre" required value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} />
            {!editing ? (
              <>
                <TextField select label="Tipo" value={form.type} onChange={(event) => {
                  const type = event.target.value;
                  setForm((current) => ({ ...current, type, normalSide: ["asset", "expense"].includes(type) ? "debit" : "credit" }));
                }}>
                  {Object.entries(ACCOUNT_TYPE_LABELS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                </TextField>
                <TextField select label="Naturaleza" value={form.normalSide} onChange={(event) => setForm((current) => ({ ...current, normalSide: event.target.value }))}>
                  <MenuItem value="debit">Deudora</MenuItem>
                  <MenuItem value="credit">Acreedora</MenuItem>
                </TextField>
              </>
            ) : null}
            <TextField label="Descripción" multiline minRows={3} value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Switch checked={form.active} onChange={(event) => setForm((current) => ({ ...current, active: event.target.checked }))} />
              <Typography>Cuenta activa</Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{saving ? "Guardando..." : "Guardar cuenta"}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
